import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, ArrowLeft, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = '';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 10) errors.push('At least 10 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('One number');
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  const passwordErrors = validatePassword(password);
  const isPasswordValid = passwordErrors.length === 0;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (error && !token) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F4EF' }}>
        <div className="w-full max-w-md px-4">
          <Card className="shadow-xl border-0 text-center">
            <CardHeader className="pb-2">
              <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-stone-900">Invalid Link</CardTitle>
              <CardDescription className="text-stone-500">
                {error}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <Link to="/forgot-password">
                <Button className="w-full bg-stone-900 hover:bg-stone-800 text-white">
                  Request New Reset Link
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F4EF' }}>
        <div className="w-full max-w-md px-4">
          <Card className="shadow-xl border-0 text-center">
            <CardHeader className="pb-2">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-stone-900">Password Reset!</CardTitle>
              <CardDescription className="text-stone-500">
                Your password has been reset successfully.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <Link to="/login">
                <Button className="w-full bg-stone-900 hover:bg-stone-800 text-white">
                  Log in with new password
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F4EF' }}>
      <div className="w-full max-w-md px-4">
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-stone-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-stone-900">Set new password</CardTitle>
            <CardDescription className="text-stone-500">
              Your new password must be different from previously used passwords.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4 pt-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-stone-700">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 pr-10"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                {/* Password requirements */}
                {password.length > 0 && (
                  <div className="space-y-1 mt-2">
                    <p className="text-xs text-stone-500 font-medium">Password requirements:</p>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        { test: password.length >= 10, label: '10+ characters' },
                        { test: /[A-Z]/.test(password), label: 'Uppercase letter' },
                        { test: /[a-z]/.test(password), label: 'Lowercase letter' },
                        { test: /[0-9]/.test(password), label: 'Number' },
                        { test: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password), label: 'Special character' },
                      ].map((req, i) => (
                        <div key={i} className={`flex items-center gap-1.5 text-xs ${req.test ? 'text-green-600' : 'text-stone-400'}`}>
                          {req.test ? <Check className="w-3 h-3" /> : <div className="w-3 h-3 rounded-full border border-current" />}
                          {req.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-stone-700">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11"
                  data-testid="reset-password-confirm"
                />
                {confirmPassword.length > 0 && (
                  <p className={`text-xs ${passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                    {passwordsMatch ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4 pt-2">
              <Button
                type="submit"
                className="w-full h-11 bg-stone-900 hover:bg-stone-800 text-white rounded-lg"
                disabled={loading || !isPasswordValid || !passwordsMatch}
                data-testid="reset-password-submit"
              >
                {loading ? 'Resetting...' : 'Reset password'}
              </Button>
              <Link to="/login" className="w-full">
                <Button variant="ghost" className="w-full text-stone-600">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to login
                </Button>
              </Link>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
